<mat-toolbar class="sidebar-header">
    <h1 class="page-title">
        <mat-icon *ngIf="title == 'trackers.edit_tracker'">edit</mat-icon>
        <mat-icon *ngIf="title !== 'trackers.edit_tracker'">add</mat-icon>

        {{title|translate}}
    </h1>

    <span class="tracker-spacer"></span>

    <lib-button (click)="dialogRef.close()" tooltip="{{'viewer.close'|translate}}" color="none" textColor="muted"
        icon="close">
    </lib-button>
</mat-toolbar>

<form class="tracker-form">
    <div class="form-scrollable">
        <div class="form-container">

            <!-- Nothing to show/copy yet on a brand new tracker - the UUID is only assigned once
                 it's actually created (see saveTracker()). -->
            <div class="badges-row" *ngIf="tracker.id">
                <div class="badge" matTooltip="{{'trackers.uuid' | translate}}">
                    <mat-icon>fingerprint</mat-icon>
                    <span class="badge-text">{{ tracker.uuid }}</span>
                    <mat-icon class="copy-btn" (click)="copy(tracker.uuid)">content_copy</mat-icon>
                </div>
            </div>

            <div class="row">
                <mat-form-field class="_full-width" appearance="outline" subscriptSizing="dynamic">
                    <mat-label>{{'form.name'|translate}}</mat-label>
                    <mat-icon matPrefix>label</mat-icon>
                    <input type="text" aria-label="text" matInput [(ngModel)]="editedTracker.name" name="name">
                </mat-form-field>
            </div>

            <div class="settings-section">
                <!-- Prefills the form below and links the template - filtered to the form's
                     current type. -->
                <div class="config-select-row">
                    <mat-form-field appearance="outline" class="full-width">
                        <mat-label>{{'resources.select_config'|translate}}</mat-label>
                        <mat-select [value]="editor.appliedTemplateId" (selectionChange)="editor.applyTemplate($event.value)"
                            name="template">
                            <mat-option [value]="undefined">{{'resources.no_template'|translate}}</mat-option>
                            <mat-option *ngFor="let config of editor.templates" [value]="config.id">{{config.name}}
                            </mat-option>
                        </mat-select>
                    </mat-form-field>
                </div>
                <div class="template-hint">{{'resources.select_kyd_template_hint'|translate}}</div>

                <mat-form-field *ngIf="editor.draft.type === 'KYD'" appearance="outline" class="full-width serial-field"
                    subscriptSizing="dynamic">
                    <mat-label>{{'trackers.serial_number'|translate}}</mat-label>
                    <mat-icon matPrefix fontIcon="confirmation_number"></mat-icon>
                    <input type="text" [(ngModel)]="editedTracker.serial" name="serial" aria-label="text" matInput>
                </mat-form-field>

                <app-tracker-config-form [config]="editor.draft" [hideName]="true" [hideCancel]="true"
                    [hideSave]="true" [hideSaveAsTemplate]="true" (save)="saveTracker()"></app-tracker-config-form>

                <!-- Confirmed, device-reported info (KYD only reports firmware/hardware version
                     over BLE) - read-only regardless of what's being configured above. -->
                <ng-container *ngIf="tracker.settings?.firmwareVersion || tracker.settings?.hardwareVersion">
                    <h4 class="p-flex">
                        <mat-icon class="header-icon-card">info</mat-icon>
                        {{'trackers.version'|translate}}
                    </h4>

                    <mat-card class="tracker-card">
                        <p class="p-flex">
                            <mat-icon>memory</mat-icon>
                            {{'trackers.firmware'|translate}} : {{tracker.settings.firmwareVersion}}
                        </p>

                        <p class="p-flex">
                            <mat-icon>devices_other</mat-icon>
                            {{'trackers.hardware'|translate}} : {{tracker.settings.hardwareVersion}}
                        </p>
                    </mat-card>
                </ng-container>
            </div>

            <div class="row">
                <lib-search-select [items]="projects" [selectedItem]="getId(editedTracker.project)"
                    [label]="projectFieldLabel" [searchPlaceholder]="'Rechercher un projet'" [valueKey]="'id'"
                    [labelKey]="'name'" (itemChanged)="onProjectChanged($event)" class="full-width">
                </lib-search-select>
                <div class="template-hint">{{'trackers.project_required_hint'|translate}}</div>
            </div>

            <div class="row">
                <mat-form-field class="_full-width" appearance="outline" *ngIf="tracker.id" subscriptSizing="dynamic">
                    <mat-label>{{'trackers.uuid'| translate}}</mat-label>
                    <mat-icon matPrefix>fingerprint</mat-icon>
                    <input type="text" [(ngModel)]="tracker.uuid" name="id" aria-label="text" matInput readonly>
                </mat-form-field>
            </div>

            <div class="row">
                <mat-form-field class="_full-width" appearance="outline" *ngIf="editedTracker.id"
                    subscriptSizing="dynamic">
                    <mat-label>{{'resources.tracker_hardware'| translate}}</mat-label>
                    <textarea matInput [(ngModel)]="editedTracker.comment" rows="2" [(ngModel)]="editedTracker.comment"
                        name="comment"></textarea>
                </mat-form-field>

                <mat-form-field class="_full-width" appearance="outline" subscriptSizing="dynamic">
                    <mat-label>{{'trackers.version'| translate}}</mat-label>
                    <textarea matInput [(ngModel)]="editedTracker.version" rows="2" [(ngModel)]="editedTracker.version"
                        name="version"></textarea>
                </mat-form-field>
            </div>

            <div class="description-stats-container">
                <div class="interventions-field">
                    <label class="interventions-label">{{'resources.tracker_linked_interventions'|translate}}</label>

                    <ng-container *ngIf="editedTracker.id; else interventionsNeedSaveFirst">
                        <div class="loading-spinner" *ngIf="loadingLinkedInterventions">
                            <app-spinner></app-spinner>
                        </div>

                        <ng-container *ngIf="!loadingLinkedInterventions">
                            <ul class="linked-interventions-list" *ngIf="linkedInterventions.length; else noLinkedInterventions">
                                <li *ngFor="let intervention of linkedInterventions">
                                    <a class="linked-intervention-item" role="button" tabindex="0"
                                        [attr.aria-label]="(intervention.category + ' — ' + intervention.subType)"
                                        (click)="openLinkedIntervention(intervention.id)"
                                        (keydown.enter)="openLinkedIntervention(intervention.id)"
                                        (keydown.space)="openLinkedIntervention(intervention.id); $event.preventDefault()">
                                        <span class="linked-intervention-category">{{ intervention.category }} — {{ intervention.subType }}</span>
                                        <span class="linked-intervention-date" *ngIf="intervention.dueDate || intervention.createdDate">
                                            {{ (intervention.dueDate || intervention.createdDate) | timeFormat }}
                                        </span>
                                    </a>
                                </li>
                            </ul>

                            <ng-template #noLinkedInterventions>
                                <p class="no-interventions">{{'resources.tracker_no_linked_interventions'|translate}}</p>
                            </ng-template>
                        </ng-container>

                        <lib-button icon="add" text="{{'resources.tracker_add_intervention'|translate}}"
                            (click)="addInterventionForTracker()"></lib-button>
                    </ng-container>

                    <ng-template #interventionsNeedSaveFirst>
                        <p class="no-interventions">{{'trackers.interventions_after_save_hint'|translate}}</p>
                    </ng-template>
                </div>

                <mat-form-field class="stats-field" appearance="outline" subscriptsizing="dynamic">
                    <mat-label>{{'trackers.stats.title'| translate}}</mat-label>
                    <app-spinner *ngIf="!stats && tracker.id" class="loading-spinner"></app-spinner>
                    <textarea matInput [(ngModel)]="stats" rows="7" name="stats" readonly></textarea>
                </mat-form-field>
            </div>
        </div>
    </div>

    <mat-dialog-actions class="sticky-actions">
        <lib-button (click)="dialogRef.close()" color="outline" icon="close" text="{{'viewer.close'|translate}}">
        </lib-button>

        <span class="tracker-spacer"></span>

        <lib-button color="secondary" (click)="onSaveAsTemplate()" icon="bookmark_add"
            text="{{'resources.save_as_template'|translate}}">
        </lib-button>

        <lib-button color="primary" (click)="saveTracker()" icon="save" text="{{'viewer.save'|translate}}">
        </lib-button>
    </mat-dialog-actions>
</form>